import { RefObject, useCallback, useEffect, useMemo } from 'react';
import { debounce, throttle } from '@direct-frontend/stdlib';
import { isDesktop } from '@direct-frontend/platform-code';

import { willOpenMobileKeyboard } from '../../../../lib/dom/willOpenMobileKeyboard/willOpenMobileKeyboard';
import { scrollIntoViewport } from '../../../../lib/dom/scrollIntoViewport';

// Константа, определяющая время в мс. через сколько пересчитать margin-bottom у ref
const PERIOD_OF_TIME_FOR_MARGIN_BOTTOM_RECALCULATION_IN_MS = 1000;

export function useViewportModalResize(ref: RefObject<HTMLElement>) {
    const updateRefMarginBottom = useCallback(() => {
        if (!window.visualViewport) {
            return;
        }

        if (!ref.current) {
            return;
        }

        const { height: visualHeight, offsetTop: visualOffset } = window.visualViewport;
        ref.current.style.marginBottom = `${window.innerHeight - visualHeight - visualOffset}px`;

        // TODO DIRECT-262812 Убрать костыль после обновления react-aria
        // Выполняем подскролл к инпуту, вызывающему отображение virtual keyboard перед следующим repaint
        const activeElement = document.activeElement;
        if (activeElement && willOpenMobileKeyboard(activeElement)) {
            requestAnimationFrame(() => {
                scrollIntoViewport(activeElement);
            });
        }
    }, [ref]);

    const updateRefMarginBottomDebounced = useMemo(() => {
        // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
        return debounce(updateRefMarginBottom, PERIOD_OF_TIME_FOR_MARGIN_BOTTOM_RECALCULATION_IN_MS);
    }, [updateRefMarginBottom]);

    const onViewportResize = useCallback(() => {
        if (!window.visualViewport) {
            return;
        }

        updateRefMarginBottom();
        // Добавляем повторный вызов функции updateRefMarginBottom() так как на iOS
        // возникает проблема с определением window.innerHeight в момент появления виртуальной
        // клавиатуры.
        updateRefMarginBottomDebounced();
    }, [updateRefMarginBottom, updateRefMarginBottomDebounced]);
    // Здесь нельзя использовать debounce, потому что отложенный вызов функции может конфликтовать
    // с useVisualViewportScrollLock на некоторых версиях мобильных браузеров и закрывать клавиатуру

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    const onThrottledViewportResize = useMemo(() => throttle(onViewportResize, 50), [onViewportResize]);

    useEffect(() => {
        if (isDesktop() || typeof window === 'undefined' || !window.visualViewport) {
            return;
        }

        window.visualViewport.addEventListener('resize', onThrottledViewportResize);

        return () => window.visualViewport?.removeEventListener('resize', onThrottledViewportResize);
    });
}
